import { z } from 'zod';

/**
 * Catégories de produits disponibles
 */
export const productCategories = [
  'Légumes',
  'Viandes',
  'Poissons',
  'Laitages',
  'Fruits',
  'Épices',
  'Condiments',
  'Conserves',
  'Produits secs',
  'Huiles et vinaigres',
  'Pâtes et riz',
  'Farines et céréales',
  'Boissons non alcoolisées',
  'Alcools',
  'Vins',
  'Bières',
  'Produits d\'entretien',
  'Produits d\'hygiène',
  'Emballages',
  'Matériel jetable',
  'Autres',
] as const;

export type ProductCategory = typeof productCategories[number];

/**
 * Unités de mesure disponibles
 */
export const productUnits = ['kg', 'g', 'L', 'mL', 'unité'] as const;
export type ProductUnit = typeof productUnits[number];

/**
 * Taux de TVA disponibles
 */
export const tvaRates = [5.5, 10, 20] as const;
export type TvaRate = typeof tvaRates[number];

/**
 * Schema pour le fournisseur
 */
export const fournisseurSchema = z.object({
  nom: z.string().min(1, 'Le nom du fournisseur est requis'),
  contact: z.string().optional(),
  email: z.string().email('Email invalide').optional().or(z.literal('')),
  telephone: z.string().optional(),
  reference: z.string().optional(),
}).optional().nullable();

export type FournisseurInput = z.infer<typeof fournisseurSchema>;

/**
 * Schema pour le stock initial lors de la création
 */
export const stockInitialSchema = z.object({
  zone: z.enum(['positive', 'negative', 'frigo', 'reserve', 'cave', 'entretien']),
  quantite: z.number().min(0, 'La quantité ne peut pas être négative'),
}).optional();

export type StockInitialInput = z.infer<typeof stockInitialSchema>;

/**
 * Schema pour la création/édition d'un produit
 */
export const productSchema = z.object({
  nom: z
    .string()
    .min(1, 'Le nom du produit est requis')
    .min(2, 'Le nom doit contenir au moins 2 caractères')
    .max(255, 'Le nom ne peut pas dépasser 255 caractères'),
  categorie: z.enum(productCategories, {
    errorMap: () => ({ message: 'Catégorie invalide' }),
  }),
  unite: z.enum(productUnits, {
    errorMap: () => ({ message: 'Unité invalide' }),
  }),
  code_barre: z
    .string()
    .max(50, 'Le code-barre ne peut pas dépasser 50 caractères')
    .nullable()
    .optional(),
  seuil_critique: z
    .number()
    .min(0, 'Le seuil critique doit être positif ou nul'),
  seuil_alerte: z
    .number()
    .min(0, 'Le seuil d\'alerte doit être positif ou nul'),
  cout_unitaire: z
    .number()
    .min(0, 'Le coût unitaire doit être positif ou nul')
    .nullable()
    .optional(),
  taux_tva: z.enum(['5.5', '10', '20']).transform(Number).or(z.number().refine(
    (val) => [5.5, 10, 20].includes(val),
    { message: 'Taux de TVA invalide' }
  )),
  fournisseur: fournisseurSchema,
  stock_initial: stockInitialSchema,
});

// Schema de base sans refinement pour pouvoir utiliser .partial()
const productBaseSchema = productSchema;

// Ajouter le refinement séparément
export const productSchemaWithValidation = productBaseSchema.refine(
  (data) => data.seuil_critique <= data.seuil_alerte,
  {
    message: 'Le seuil critique doit être inférieur ou égal au seuil d\'alerte',
    path: ['seuil_critique'],
  }
);

export type ProductInput = z.infer<typeof productSchemaWithValidation>;

/**
 * Schema pour la mise à jour d'un produit (tous les champs optionnels)
 */
export const productUpdateSchema = z.object({
  nom: z
    .string()
    .min(2, 'Le nom doit contenir au moins 2 caractères')
    .max(255, 'Le nom ne peut pas dépasser 255 caractères')
    .optional(),
  categorie: z.enum(productCategories).optional(),
  unite: z.enum(productUnits).optional(),
  code_barre: z.string().max(50).nullable().optional(),
  seuil_critique: z.number().min(0).optional(),
  seuil_alerte: z.number().min(0).optional(),
  cout_unitaire: z.number().min(0).nullable().optional(),
  taux_tva: z.number().optional(),
  fournisseur: fournisseurSchema,
});

export type ProductUpdateInput = z.infer<typeof productUpdateSchema>;

/**
 * Schema pour la recherche de produits
 */
export const productSearchSchema = z.object({
  query: z.string().optional(),
  categorie: z.enum(productCategories).optional(),
  zone: z.enum(['positive', 'negative', 'frigo', 'reserve', 'cave', 'entretien']).optional(),
  enRupture: z.boolean().optional(),
  enAlerte: z.boolean().optional(),
});

export type ProductSearchInput = z.infer<typeof productSearchSchema>;
